import {Id, Uuid} from "../../types";
import {WorkflowState} from "./workflow_state";
import {
  WorkflowInput,
  WorkflowMetaOverrides,
  ContextId,
  WorkflowId,
} from "./workflow";
import {DocumentIssueId, IssueId} from "../../../common/types/issue";
import {WorkflowTask} from "./task_config";
import {GroupedResultEntry, GroupedResults, Test, TestResult} from "./test";
import {
  ProblemComplexityKey,
  QueryDomain,
} from "../../../common/types/query_domain";
import {UserId} from "common/types/user";
import {DocumentId} from "common/types/document";
import {ProjectId} from "common/types/project";
export type LlmRunId = Id;
export type RunId = Uuid;
export type BatchId = string;

export interface LlmRunInfo {
  id: LlmRunId;
  run_id: RunId;
}

export interface LlmRun extends LlmRunInfo {
  data: WorkflowState;
  has_failed: boolean;
  is_complete: boolean;
  username: string | null;
}

export interface EnhancedLlmRun extends LlmRun {
  workflow_id: WorkflowId;
  workflow_name: string;
  workflow_context_id: ContextId;
  inputs: WorkflowInput[];
  tasks: WorkflowTask[];
  overrides: WorkflowMetaOverrides;
  links: Record<string, unknown>;
  creation_reason: string;
  creation_date: Date;
  tests: Test[];
  grouped_results: GroupedResults<GroupedResultEntry>;
  test_results?: GroupedResults<LlmRunTestResults>;
  child_cost: number;
  parent_cost: number;
  child_run_count: number;
  task_workflow_mapping: Record<number, number>;
}

export interface LlmRunForComparison extends LlmRun {
  workflow_id: WorkflowId;
  workflow_name: string;
  workflow_context_id: ContextId;
  inputs: WorkflowInput[];
  links: Record<string, unknown>;
  creation_reason: string;
  creation_date: Date;
  results: GroupedResults<TestResult>;
  cost: number;
}

export interface LlmRunTestResults {
  llm_run_id: LlmRunId;
  results: TestResult[];
}

export const llmRunNumberFilterKeys = [
  "workflow",
  "project",
  "document",
  "issue",
  "clausepart",
] as const;
export const llmRunStringFilterKeys = ["parent", "batch", "user"] as const;
export const llmRunStringArrayFilterKeys = ["reasons", "status"] as const;
export const llmRunOtherFilterKeys = [
  "from",
  "to",
  "min_cost",
  "max_runs",
  "input_contains",
] as const;

export const llmRunFilterKeys = [
  ...llmRunNumberFilterKeys,
  ...llmRunStringFilterKeys,
  ...llmRunStringArrayFilterKeys,
  ...llmRunOtherFilterKeys,
];

export type LlmRunNumberFilterKeys = typeof llmRunNumberFilterKeys[number];
export type LlmRunStringFilterKeys = typeof llmRunStringFilterKeys[number];
export type LlmRunStringArrayFilterKeys = typeof llmRunStringArrayFilterKeys[number];
export type LlmRunOtherFilterKeys = typeof llmRunOtherFilterKeys[number];

export type LlmRunFilterKeys =
  | LlmRunNumberFilterKeys
  | LlmRunStringFilterKeys
  | LlmRunStringArrayFilterKeys
  | LlmRunOtherFilterKeys;
export type LlmRunNumberFilters = {
  [key in LlmRunNumberFilterKeys]: number | null;
};
export type LlmRunStringFilters = {
  [key in LlmRunStringFilterKeys]: number | null;
};
export type LlmRunStringArrayFilters = {
  // When URL queries are parsed, arrays with a single value are treated as strings.
  [key in LlmRunStringArrayFilterKeys]: string | string[] | null;
};
export type LlmRunOtherFilters = {
  [key in LlmRunOtherFilterKeys]: Date | string | number | null;
};
export type LlmRunFilters = LlmRunNumberFilters &
  LlmRunStringFilters &
  LlmRunStringArrayFilters &
  LlmRunOtherFilters;

export interface SingleTestResult {
  key: string;
  state: "pass" | "fail" | null;
}

export interface LlmRunScore {
  batch_id: BatchId;
  llm_run_id: LlmRunId;
  document_issue_id: DocumentIssueId;
  project_id: ProjectId;
  document_id: DocumentId;
  issue_id: IssueId;
  project_name: string;
  document_name: string;
  issue_name: string;
  problem_complexity_pass_state?: Record<
    ProblemComplexityKey,
    {results: SingleTestResult[]}
  >;
  query_domain: QueryDomain;
}

export interface LlmRunBatchInfo {
  batch_id: BatchId;
  user_id: UserId;
  username: string;
  first_run: Date;
  runs: number;
  different_issues: Array<{id: number; name: string}>;
  different_projects: Array<{id: number; name: string}>;
  different_documents: Array<{id: number; name: string}>;
}
